/**
 * ゲーム状態管理（Phaser 非依存）。
 * 入力・物理・ゲームオーバー判定は必ずこの状態を参照して実行する。
 */

export const GameState = {
  LOADING: 'LOADING',
  READY: 'READY',
  PLAYING: 'PLAYING',
  PAUSED: 'PAUSED',
  GAME_OVER: 'GAME_OVER',
} as const;

export type GameStateValue = (typeof GameState)[keyof typeof GameState];

/** 許可された遷移のみを通す。 */
const TRANSITIONS: Record<GameStateValue, GameStateValue[]> = {
  LOADING: ['READY'],
  READY: ['PLAYING', 'LOADING'],
  PLAYING: ['PAUSED', 'GAME_OVER', 'READY'],
  PAUSED: ['PLAYING', 'READY', 'GAME_OVER'],
  GAME_OVER: ['READY', 'PLAYING'],
};

export type StateChangeListener = (to: GameStateValue, from: GameStateValue) => void;

export class GameStateMachine {
  private current: GameStateValue;
  private readonly listeners = new Set<StateChangeListener>();

  constructor(initial: GameStateValue = GameState.LOADING) {
    this.current = initial;
  }

  get state(): GameStateValue {
    return this.current;
  }

  is(...states: GameStateValue[]): boolean {
    return states.includes(this.current);
  }

  canTransition(to: GameStateValue): boolean {
    if (to === this.current) return false;
    return TRANSITIONS[this.current].includes(to);
  }

  /** 遷移できた場合のみ true を返す。 */
  transition(to: GameStateValue): boolean {
    if (!this.canTransition(to)) return false;
    const from = this.current;
    this.current = to;
    this.listeners.forEach((listener) => listener(to, from));
    return true;
  }

  onChange(listener: StateChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** 入力を受け付けてよい状態か。 */
  get acceptsInput(): boolean {
    return this.current === GameState.PLAYING;
  }

  /** 物理演算・ゲームオーバー判定を進めてよい状態か。 */
  get simulates(): boolean {
    return this.current === GameState.PLAYING;
  }
}
